// ═══════════════════════════════════════════════════════════════════════════
// Phase 2 — typed-ordinal constraint graph parser for ACDBASSOC2DCONSTRAINTGROUP.
// ═══════════════════════════════════════════════════════════════════════════
//
// CONTEXT
// ───────
// After the `360 <childHandle>` list in an ACDBASSOC2DCONSTRAINTGROUP body,
// AutoCAD encodes a typed-ordinal graph describing the solver topology:
//
//   Section A — class declaration table:
//     1 <ClassName>
//     90 <ordinal>
//     …repeated…
//
//   Section B — per-ordinal bodies (in the SAME ORDER as Section A):
//     90 <selfOrdinal|-1 for internal connectors>
//     90 <arity>
//     90 <refOrdinal> × arity
//     70 <flag>
//     [330 <entityHandle|0>]        (curve / datum bodies)
//     [90 <secondaryFlag>]
//     [10/20/30/11/21/31 <float>]   (geometry basis — skipped)
//     [290 <flag>] × N              (trailing metadata)
//     [340 <valueDepHandle>] × N    (dim-constraint bodies carry 2 of these)
//     [280 <flag>]
//
// The Phase-2 pipeline extracts:
//   - ordinal → className              (Section A)
//   - ordinal → refs[]                 (Section B header)
//   - ordinal → entityHandle           (Section B first 330, if curve class)
//   - ordinal → softRefs[] (340s)      (Section B tail, dim-constraint class)
//   - softRefHandle → dimOrdinal       (reverse map of the above)
//
// These indices power per-parameter narrowing in `resolveAssociatedEntities`
// (Tier 1). Tier 2 (chain-match) and Tier 3 (group-fallback) remain as safety
// nets when the graph is malformed or absent.
//
// The parser follows a "skip-on-fail" contract: if ANY stage throws or
// yields an unexpected shape, `parseConstraintGraph` returns `null` and the
// caller proceeds without graph-based narrowing (no regression vs Phase 1).

import type { DxfGroup } from "@dxf-ai-editor/dxf-io";

// ═══════════════════════════════════════════════════════════════════════════
// Typed structures
// ═══════════════════════════════════════════════════════════════════════════

export interface ConstraintGraphNode {
  readonly ordinal: number;
  readonly className: string;
  readonly refs: readonly number[];
  readonly entityHandle: string; // "" if not a curve-like class
  readonly softRefs: readonly string[]; // 340 handles (value-dep refs) for dim-constraints
}

export interface Assoc2dConstraintGroupGraph {
  readonly nodesByOrdinal: ReadonlyMap<number, ConstraintGraphNode>;
  readonly valueDepToOrdinal: ReadonlyMap<string, number>;
}

// ═══════════════════════════════════════════════════════════════════════════
// Class discriminators (AutoCAD AcConstraint* internal names)
// ═══════════════════════════════════════════════════════════════════════════

export function isCurveClass(name: string): boolean {
  return (
    name === "AcConstrainedBoundedLine" ||
    name === "AcConstrainedArc" ||
    name === "AcConstrainedCircle" ||
    name === "AcConstrainedSpline" ||
    name === "AcConstrainedEllipse" ||
    name === "AcConstrainedDatumLine" ||
    name === "AcConstrainedRay" ||
    name === "AcConstrainedXline"
  );
}

export function isPointClass(name: string): boolean {
  return (
    name === "AcConstrainedImplicitPoint" ||
    name === "AcConstrainedDatumPoint"
  );
}

export function isDimConstraintClass(name: string): boolean {
  return (
    name === "AcDistanceConstraint" ||
    name === "AcHorizontalConstraint" ||
    name === "AcVerticalConstraint" ||
    name === "AcRadiusDiameterConstraint" ||
    name === "AcAngleConstraint" ||
    name === "AcAngle3PointConstraint"
  );
}

/**
 * Internal "glue" connector class — binds an implicit / datum point to
 * a curve. Has refs but no entity handle. When walking dim → point →
 * curve, the path goes through `AcPointCurveConstraint` (one extra hop
 * per point→curve edge) for the vast majority of AutoCAD-authored
 * constraint graphs.
 */
export function isPointCurveConnectorClass(name: string): boolean {
  return name === "AcPointCurveConstraint";
}

// ═══════════════════════════════════════════════════════════════════════════
// Section A — class declaration parser
// ═══════════════════════════════════════════════════════════════════════════

interface ClassDecl {
  className: string;
  ordinal: number;
}

/**
 * Scan `groups` starting at `startIdx` and consume consecutive
 * `1 <className>` + `90 <ordinal>` pairs. Returns the list of declarations
 * and the index of the first group that does NOT fit the pattern (= start
 * of Section B).
 *
 * The caller supplies `startIdx` as the first index AFTER the last `360`
 * child handle and its trailing `90 <something>` + `290 <flag>` metadata.
 * We scan forward, skipping any non-1/non-90 codes until we hit a `1`
 * code, then alternate `1`/`90` until the pattern breaks.
 */
function parseSectionA(
  groups: readonly DxfGroup[],
  startIdx: number,
): { decls: ClassDecl[]; endIdx: number } {
  const decls: ClassDecl[] = [];

  // Advance to the first `1 <className>` code — AutoCAD puts various
  // `90 <meta>` and `290 <flag>` codes between the 360 list and Section A
  // (e.g. `90 369  90 0  90 0  290 0  90 339` in АБИ1 4DED).
  let i = startIdx;
  while (i < groups.length) {
    const g = groups[i];
    if (!g) break;
    if (g.code === 1) break;
    i++;
  }

  // Alternate 1/90 pairs.
  while (i + 1 < groups.length) {
    const nameGroup = groups[i];
    const ordGroup = groups[i + 1];
    if (!nameGroup || !ordGroup) break;
    if (nameGroup.code !== 1) break;
    if (ordGroup.code !== 90) break;
    const className = nameGroup.value.trim();
    const ordinal = Number.parseInt(ordGroup.value.trim(), 10);
    if (!Number.isFinite(ordinal)) break;
    decls.push({ className, ordinal });
    i += 2;
  }

  return { decls, endIdx: i };
}

// ═══════════════════════════════════════════════════════════════════════════
// Section B — per-ordinal body parser
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Parse Section B. Walk codes linearly. Each body has the shape:
 *   90 selfOrd
 *   90 arity
 *   (90 ref) × arity
 *   70 flag
 *   <tail: 330, 90, 10/20/30, 290, 340, 280 in variable order>
 *
 * The body terminates when we encounter the NEXT `90` that is the start
 * of a new body header (confirmed by seeing `70` again after consuming
 * `90 selfOrd 90 arity 90×arity refs`).
 *
 * We track body index positionally — body[i] corresponds to Section A's
 * i-th class declaration. The `90 -1` self-indicator on PointCurveConstraint
 * bodies is accepted as-is; the ordinal mapping comes from Section A.
 */
function parseSectionB(
  groups: readonly DxfGroup[],
  startIdx: number,
  sectionA: readonly ClassDecl[],
): ConstraintGraphNode[] {
  const nodes: ConstraintGraphNode[] = [];
  if (sectionA.length === 0) return nodes;

  let i = startIdx;

  for (let bodyIdx = 0; bodyIdx < sectionA.length; bodyIdx++) {
    const decl = sectionA[bodyIdx];
    if (!decl) break;

    // ── Step 1: consume header — first two 90s (selfOrd, arity), then arity more 90s (refs), ending at 70. ──
    // Some bodies have additional trailing `90` codes after the refs
    // (observed in certain classes). The reliable terminator is the first
    // `70` code.

    // Skip any leading non-90 codes (should be rare — typically body starts with 90 immediately).
    while (i < groups.length) {
      const g = groups[i];
      if (!g) break;
      if (g.code === 90) break;
      // If we see something unexpected before the 90 selfOrd, abort.
      if (g.code === 1) throw new Error(`[graph] unexpected code 1 before body header at idx ${i}`);
      i++;
    }

    if (i >= groups.length) break;

    // Collect all consecutive 90s until the first 70.
    const headerNinties: number[] = [];
    while (i < groups.length) {
      const g = groups[i];
      if (!g) break;
      if (g.code === 70) break;
      if (g.code === 90) {
        const n = Number.parseInt(g.value.trim(), 10);
        headerNinties.push(Number.isFinite(n) ? n : 0);
        i++;
        continue;
      }
      // Anything else in the header (e.g. 330, 10) means we've mis-aligned.
      // Abort parsing to trigger skip-on-fail at the caller.
      throw new Error(
        `[graph] unexpected code ${g.code} inside body header at idx ${i}`,
      );
    }

    if (i >= groups.length) {
      throw new Error(`[graph] EOF before body flag at body ${bodyIdx}`);
    }

    // headerNinties = [selfOrd, arity, ref0, …, refN-1]. arity = headerNinties[1].
    if (headerNinties.length < 2) {
      throw new Error(
        `[graph] body ${bodyIdx} header has <2 ninties (got ${headerNinties.length})`,
      );
    }
    const arity = headerNinties[1]!;
    if (arity < 0 || arity > 1000) {
      throw new Error(`[graph] body ${bodyIdx} arity out of bounds: ${arity}`);
    }
    // refs are positions 2..2+arity (exclusive). When fewer refs are
    // available than arity suggests (possible with trailing `90 <meta>`
    // codes that our peek didn't catch), take what we have and continue
    // instead of aborting — graph quality degrades gracefully. The skip-
    // on-fail tier-2/3 net still exists for catastrophic mis-alignment.
    const refs: number[] = [];
    const expectedRefEnd = 2 + arity;
    const actualRefEnd = Math.min(expectedRefEnd, headerNinties.length);
    for (let r = 2; r < actualRefEnd; r++) {
      const ord = headerNinties[r];
      if (ord !== undefined) refs.push(ord);
    }

    // Consume the 70 flag.
    i++;

    // ── Step 2: consume body tail until the next header start. ──
    //
    // A header start is detected by two consecutive 90 codes followed
    // eventually by a 70 — but to avoid lookahead complexity, we use a
    // simpler invariant: body tail codes are any of
    // 330 / 90 / 10 / 20 / 30 / 11 / 21 / 31 / 40 / 290 / 340 / 280 / 70
    // EXCEPT we detect body transition when we hit a "pure header run" —
    // a 90 code whose value could be a new selfOrd, where the previous
    // body has already emitted its terminator signals (280 or geometry
    // completion).
    //
    // Easier approach: we know the body has consumed header + flag. The
    // tail ends when we encounter a `90` code followed by another `90` code
    // that IS a valid header for the next body. To detect this cheaply:
    // after consuming at least ONE non-90 code (flag already consumed is
    // 70), the next run of 90s is the next header.
    //
    // Even simpler: scan forward and collect 330/340 codes until we see
    // the pattern `90 90` without any non-90 in between. The first such
    // pattern marks the next body header.

    let entityHandle = "";
    const softRefs: string[] = [];
    let sawEntityHandle = false;

    // Walk the tail. We stop when we see the next `90 … 90 …` run with
    // no interleaved non-90 code. Tracking: remember the last non-90
    // tail code we saw; if we see `90` AND the one before the current
    // code was ALSO a `90`, we've either (a) seen a header run, or
    // (b) a multi-value 90 sequence inside the tail.
    //
    // The discriminator: bodies always have a non-90 code (like 70 or
    // 280 or 10 or 290) between header-90s and tail-90s. Once we've
    // entered the tail, each 90 is followed by a non-90 code.

    while (i < groups.length) {
      const g = groups[i];
      if (!g) break;

      if (g.code === 90) {
        // Is this the start of the next body header? Peek ahead — if
        // the immediate next code is also 90, and the one after begins
        // with a short-arity value (<=50 typical, though refs can be up
        // to ~360 — for simpler detection, use a heuristic), we've hit
        // the next header.
        //
        // Better heuristic: tail-90s inside a body are always followed
        // by another specific non-90 code in the same body (280, 290,
        // 330, 10). A HEADER-90 is followed by another 90 (arity) which
        // in turn is followed by more 90s (refs) then a 70.
        //
        // Peek: if the NEXT non-90 code within a bounded lookahead is
        // `70`, we're in a header. If it's `330`/`10`/`280`/`290`/`340`,
        // we're still in a tail (unlikely in this grammar but defensive).

        let peekIdx = i + 1;
        let seenExtra90 = false;
        while (peekIdx < groups.length) {
          const p = groups[peekIdx];
          if (!p) break;
          if (p.code === 90) {
            seenExtra90 = true;
            peekIdx++;
            continue;
          }
          // Non-90 encountered. If seenExtra90 AND this is 70 → next body header.
          if (seenExtra90 && p.code === 70) {
            // This IS the next body. Stop consuming tail; don't advance i.
            break;
          }
          // If we see anything other than 90/70 right after a single 90,
          // that 90 is a tail value (e.g. `90 <secondaryFlag>` before 290).
          break;
        }
        if (peekIdx < groups.length) {
          const nextNon90 = groups[peekIdx];
          if (nextNon90 && nextNon90.code === 70 && peekIdx > i + 1) {
            // Stop — next body header detected.
            break;
          }
        }

        // Otherwise, this is a tail `90 <flag>` — consume and move on.
        i++;
        continue;
      }

      if (g.code === 330) {
        // First 330 in the body is the entity handle (or "0"). For
        // non-curve classes this is `330 0`.
        const h = g.value.trim();
        if (!sawEntityHandle) {
          entityHandle = h === "0" ? "" : h;
          sawEntityHandle = true;
        }
        i++;
        continue;
      }

      if (g.code === 340) {
        const h = g.value.trim();
        if (h.length > 0 && h !== "0") softRefs.push(h);
        i++;
        continue;
      }

      // Geometry, flags, counts, etc. — skip.
      // (10/20/30/11/21/31/40 floats; 70/280/290 flags; 100 subclass)
      i++;
    }

    nodes.push({
      ordinal: decl.ordinal,
      className: decl.className,
      refs,
      entityHandle,
      softRefs,
    });
  }

  return nodes;
}

// ═══════════════════════════════════════════════════════════════════════════
// Public entry point — attempts full parse, returns null on failure.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Parse the typed-ordinal graph out of an ACDBASSOC2DCONSTRAINTGROUP body.
 *
 * `groups` = full raw DXF groups of the group record (including the `0`
 * header). `childListEndIdx` = the index after the last `360 <child>`
 * group where Section A begins (caller is responsible for walking past
 * the initial 360 list).
 *
 * Returns a fully-populated `Assoc2dConstraintGroupGraph` on success, or
 * `null` when parsing fails (caller falls back to tier-2 / tier-3).
 */
export function parseConstraintGraph(
  groups: readonly DxfGroup[],
  childListEndIdx: number,
): Assoc2dConstraintGroupGraph | null {
  try {
    const { decls, endIdx: sectionBStart } = parseSectionA(
      groups,
      childListEndIdx,
    );
    if (decls.length === 0) return null;

    const parsedNodes = parseSectionB(groups, sectionBStart, decls);
    if (parsedNodes.length === 0) return null;

    const nodesByOrdinal = new Map<number, ConstraintGraphNode>();
    const valueDepToOrdinal = new Map<string, number>();
    for (const node of parsedNodes) {
      nodesByOrdinal.set(node.ordinal, node);
      if (isDimConstraintClass(node.className)) {
        for (const h of node.softRefs) {
          if (!valueDepToOrdinal.has(h)) {
            valueDepToOrdinal.set(h, node.ordinal);
          }
        }
      }
    }

    return { nodesByOrdinal, valueDepToOrdinal };
  } catch (err) {
    // Skip-on-fail: log once for telemetry, return null so tier-2/3 run.
    console.warn(
      `[DYN-6 graph] parseConstraintGraph failed — falling back to tier-2/3`,
      err instanceof Error ? err.message : String(err),
    );
    return null;
  }
}
